import { Skeleton } from "@bednbody/ui/components/skeleton";
import { AdminDataTable, AdminTableIdentity } from "@bednbody/ui/components/admin-data-table";
import { Badge } from "@bednbody/ui/components/badge";
import { Button } from "@bednbody/ui/components/button";
import { Callout } from "@bednbody/ui/components/callout";
import { RetryButton } from "@bednbody/ui/components/retry-button";
import { Switch } from "@bednbody/ui/components/switch";
import type { ColumnDef } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  type OperatingSettings,
  type Rank,
  RANKS,
  useOperatingSettings,
  useUpdateOperatingSettings,
} from "../settings/settings.hooks";
import { CancellationShareCard } from "../settings/CancellationShareCard";
import { NumberInput } from "../settings/settingsFields";
import { TIER_ROLES, type TierRole } from "./rankings.hooks";

/**
 * Tab 3 — what each rank is worth. One row per rank, per role.
 *
 *   • Minimum days to secure — therapists (0217); riders do not secure weekly
 *     sessions, so their rows show a dash.
 *   • Cancellation share — the column is a summary; the rule itself (mode,
 *     percentages, eligible ranks) is the card under the table. It moved here from
 *     Settings because eligibility is decided by rank. The card keeps its own save:
 *     those fields are validated together and must not be split across two forms.
 *   • Can secure early — NEW (0332). Therapists only. A rank may start securing
 *     its week this many hours before everyone else. OFF with no number for every
 *     rank: Luigi has not approved any value, so none is suggested here.
 */

interface PerkRow {
  key: string;
  role: TierRole;
  rank: Rank;
  days: string;
  early: boolean;
  hours: string;
}

const rankLabel = (rank: string) => rank.charAt(0).toUpperCase() + rank.slice(1);
const roleLabel = (role: TierRole) => (role === "therapist" ? "Therapist" : "Rider");

const DAYS_KEY = {
  bronze: "min_secured_bronze",
  silver: "min_secured_silver",
  gold: "min_secured_gold",
  diamond: "min_secured_diamond",
} as const;
const EARLY_KEY = {
  bronze: "early_securing_hours_bronze",
  silver: "early_securing_hours_silver",
  gold: "early_securing_hours_gold",
  diamond: "early_securing_hours_diamond",
} as const;

function toRows(settings: OperatingSettings): PerkRow[] {
  return TIER_ROLES.flatMap((role) =>
    RANKS.map((rank) => {
      const hours = settings[EARLY_KEY[rank]];
      return {
        key: `${role}/${rank}`,
        role,
        rank,
        days: role === "therapist" ? String(settings[DAYS_KEY[rank]]) : "",
        early: role === "therapist" && hours !== null,
        hours: role === "therapist" && hours !== null ? String(hours) : "",
      };
    }),
  );
}

function PerksTable({ settings }: { settings: OperatingSettings }) {
  const update = useUpdateOperatingSettings();
  const [draft, setDraft] = useState<PerkRow[]>(() => toRows(settings));
  useEffect(() => setDraft(toRows(settings)), [settings]);

  const patchRow = (key: string, change: Partial<PerkRow>) =>
    setDraft((rows) => rows.map((r) => (r.key === key ? { ...r, ...change } : r)));

  function save() {
    const patch: Parameters<typeof update.mutate>[0] = {};
    for (const row of draft.filter((r) => r.role === "therapist")) {
      const days = Number(row.days);
      if (row.days.trim() === "" || !Number.isInteger(days) || days < 0 || days > 7) {
        toast.error(`${rankLabel(row.rank)}: minimum days has to be a whole number, 0 to 7.`);
        return;
      }
      let hours: number | null = null;
      if (row.early) {
        hours = Number(row.hours);
        if (row.hours.trim() === "" || !Number.isInteger(hours) || hours < 1) {
          toast.error(
            `${rankLabel(row.rank)}: early securing needs a whole number of hours, 1 or more.`,
          );
          return;
        }
      }
      patch[DAYS_KEY[row.rank]] = days;
      patch[EARLY_KEY[row.rank]] = hours;
    }
    update.mutate(patch, {
      onSuccess: () => toast.success("Perks saved."),
      onError: (error) => toast.error(error.message),
    });
  }

  const cancellationEligible = (row: PerkRow) =>
    (row.role === "therapist"
      ? settings.cancellation_share_therapist_ranks
      : settings.cancellation_share_rider_ranks
    ).includes(row.rank);

  const columns = useMemo<ColumnDef<PerkRow, unknown>[]>(
    () => [
      {
        id: "rank",
        accessorFn: (row) => `${row.role} ${RANKS.indexOf(row.rank)}`,
        header: "Rank",
        cell: ({ row }) => (
          <AdminTableIdentity
            title={rankLabel(row.original.rank)}
            subtitle={roleLabel(row.original.role)}
            fallback={roleLabel(row.original.role).charAt(0)}
          />
        ),
      },
      {
        id: "days",
        accessorFn: (row) => (row.role === "therapist" ? Number(row.days) : -1),
        header: "Minimum days to secure",
        cell: ({ row }) =>
          row.original.role === "therapist" ? (
            <NumberInput
              className="w-24"
              value={row.original.days}
              onChange={(value) => patchRow(row.original.key, { days: value })}
            />
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: "cancellation",
        accessorFn: (row) => (cancellationEligible(row) ? 1 : 0),
        header: "Cancellation share",
        cell: ({ row }) =>
          cancellationEligible(row.original) ? (
            <Badge variant="success-soft">Eligible</Badge>
          ) : (
            <Badge variant="muted-soft">Not eligible</Badge>
          ),
      },
      {
        id: "early",
        enableSorting: false,
        header: "Can secure early",
        cell: ({ row }) =>
          row.original.role === "therapist" ? (
            <div className="flex items-center gap-3">
              <Switch
                aria-label={`Early securing for ${rankLabel(row.original.rank)} therapists`}
                checked={row.original.early}
                onCheckedChange={(checked) =>
                  patchRow(row.original.key, {
                    early: checked,
                    hours: checked ? row.original.hours : "",
                  })
                }
              />
              {row.original.early ? (
                <>
                  <NumberInput
                    className="w-24"
                    value={row.original.hours}
                    onChange={(value) => patchRow(row.original.key, { hours: value })}
                  />
                  <span className="text-xs text-muted-foreground">hours earlier</span>
                </>
              ) : (
                <span className="text-xs text-muted-foreground">Off</span>
              )}
            </div>
          ) : (
            <span className="text-muted-foreground">Not applicable</span>
          ),
      },
    ],
    // `cancellationEligible` reads `settings`; `patchRow` only uses the setter.
    [settings],
  );

  return (
    <div className="space-y-8">
      <Callout title="Perks by rank">
        <p>
          Early securing lets a rank start choosing its week's days that many hours before everyone
          else opens.
        </p>
        <p>
          It is off for every rank until Luigi approves a value. It sits on top of the existing
          Diamond priority window rather than replacing it.
        </p>
      </Callout>
      <AdminDataTable
        presentation="standalone"
        data={draft}
        columns={columns}
        getRowId={(row) => row.key}
        searchText={(row) => `${row.role} ${row.rank}`}
        searchPlaceholder="Search ranks…"
        exportFilename="rank-perks"
        exportColumns={[
          { header: "Role", value: (row) => row.role },
          { header: "Rank", value: (row) => row.rank },
          {
            header: "Minimum days to secure",
            value: (row) => (row.role === "therapist" ? row.days : ""),
          },
          {
            header: "Cancellation share",
            value: (row) => (cancellationEligible(row) ? "eligible" : "not eligible"),
          },
          { header: "Early securing hours", value: (row) => (row.early ? row.hours : "off") },
        ]}
        facets={[
          {
            id: "role",
            label: "Role",
            options: TIER_ROLES.map((role) => ({ value: role, label: roleLabel(role) })),
            getValue: (row) => row.role,
          },
        ]}
        header={
          <Button onClick={save} disabled={update.isPending}>
            Save perks
          </Button>
        }
        emptyTitle="No ranks"
        emptyDescription="Perks cannot be edited until the settings load."
      />
    </div>
  );
}

export function PerksTab() {
  const settings = useOperatingSettings();

  if (settings.isError) {
    return (
      <div className="space-y-3 p-4">
        <p className="text-sm text-muted-foreground">
          The settings could not be loaded. That is a failed read, not an empty setting.
        </p>
        <RetryButton size="sm" onRetry={() => settings.refetch()} />
      </div>
    );
  }
  if (!settings.data) return <Skeleton className="h-48 motion-reduce:animate-none" />;
  return (
    <div className="space-y-8">
      <PerksTable settings={settings.data} />
      <CancellationShareCard settings={settings.data} />
    </div>
  );
}
